import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, ShieldAlert, ShieldQuestion, Search, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerificarPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [protocolo, setProtocolo] = useState(params.get("p") || "");
  const [hash, setHash] = useState(params.get("h") || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const verificar = async (p, h) => {
    if (!p || !h) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/verificar/${encodeURIComponent(p)}?h=${encodeURIComponent(h)}`);
      setResult(r.data);
    } catch (e) {
      setResult({ valido: false, motivo: "Erro ao consultar servidor" });
    } finally {
      setLoading(false);
    }
  };

  // Auto-verifica se veio via QR code
  useEffect(() => {
    if (params.get("p") && params.get("h")) verificar(params.get("p"), params.get("h"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    verificar(protocolo.trim(), hash.trim());
  };

  return (
    <div className="min-h-screen bg-[#F4F6F9]">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Button variant="ghost" onClick={() => nav("/")} className="mb-4" data-testid="btn-voltar">
          <ArrowLeft className="w-4 h-4 mr-1" />Voltar
        </Button>

        <div className="mb-8">
          <div className="text-xs uppercase tracking-wider text-[#00883A] font-bold mb-1 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" />Autenticidade do Documento
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-[#0F172A]">Verificar Comprovante</h1>
          <p className="text-sm text-slate-600 mt-2">
            Confirme a autenticidade de um comprovante de pré-inscrição MCMV. Escaneie o QR do PDF
            ou informe manualmente o protocolo e a assinatura digital abaixo.
          </p>
        </div>

        <Card>
          <CardHeader><CardTitle className="text-base">Dados para verificação</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-3">
              <div>
                <Label htmlFor="p" className="text-xs">Protocolo</Label>
                <Input
                  id="p"
                  value={protocolo}
                  onChange={(e) => setProtocolo(e.target.value)}
                  placeholder="GRAJAU-MCMV-2026-000001"
                  data-testid="verify-protocolo"
                  className="font-mono"
                />
              </div>
              <div>
                <Label htmlFor="h" className="text-xs">Assinatura digital</Label>
                <Input
                  id="h"
                  value={hash}
                  onChange={(e) => setHash(e.target.value.toUpperCase())}
                  placeholder="16 caracteres hexadecimais"
                  data-testid="verify-hash"
                  className="font-mono"
                  maxLength={40}
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !protocolo || !hash}
                className="bg-[#083CA6] hover:bg-[#062f85]"
                data-testid="btn-verificar"
              >
                <Search className="w-4 h-4 mr-2" />{loading ? "Verificando..." : "Verificar autenticidade"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {result && (
          <Card className={`mt-6 ${result.valido ? "border-2 border-green-500 bg-green-50/50" : "border-2 border-red-500 bg-red-50/50"}`}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                {result.valido ? (
                  <ShieldCheck className="w-12 h-12 text-green-600 flex-shrink-0" />
                ) : (
                  <ShieldAlert className="w-12 h-12 text-red-600 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <h2 className={`text-xl font-black ${result.valido ? "text-green-800" : "text-red-800"}`}>
                    {result.valido ? "Documento AUTÊNTICO" : "Documento NÃO PODE SER VALIDADO"}
                  </h2>
                  {result.valido ? (
                    <div className="mt-3 space-y-2 text-sm">
                      <div className="grid grid-cols-[140px_1fr] gap-2">
                        <span className="text-slate-600 font-medium">Protocolo:</span>
                        <span className="font-mono">{result.protocolo}</span>
                        <span className="text-slate-600 font-medium">Nome:</span>
                        <span className="font-semibold">{result.nome}</span>
                        <span className="text-slate-600 font-medium">CPF:</span>
                        <span className="font-mono">{result.cpf_mascarado}</span>
                        <span className="text-slate-600 font-medium">Emitido em:</span>
                        <span>{new Date(result.created_at).toLocaleString("pt-BR")}</span>
                        <span className="text-slate-600 font-medium">Status atual:</span>
                        <span><Badge className="bg-blue-100 text-blue-800">{result.status}</Badge></span>
                        <span className="text-slate-600 font-medium">Apto Faixa 1:</span>
                        <span>
                          {result.apta_faixa1 ? (
                            <Badge className="bg-green-100 text-green-800">Sim</Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-800">Não</Badge>
                          )}
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 mt-4 pt-4 border-t border-green-200">
                        Este comprovante foi emitido pela Prefeitura Municipal de Grajaú-MA e
                        seus dados-chave (protocolo, CPF, nome, data e renda) não foram alterados
                        desde a emissão.
                      </p>
                    </div>
                  ) : (
                    <div className="mt-3 space-y-2 text-sm text-red-700">
                      <p><strong>Motivo:</strong> {result.motivo}</p>
                      <p className="text-xs text-slate-700 mt-4 pt-4 border-t border-red-200">
                        Se o QR Code deste PDF não valida, o documento pode ter sido adulterado
                        ou não foi emitido oficialmente pela Prefeitura de Grajaú. Em caso de
                        dúvida, procure a Secretaria de Habitação (SEHAB).
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!result && !loading && (
          <Card className="mt-6 border-dashed">
            <CardContent className="p-8 text-center text-slate-500">
              <ShieldQuestion className="w-12 h-12 mx-auto mb-2 text-slate-400" />
              <p className="text-sm">
                Aguardando dados. Informe o protocolo e a assinatura digital que constam no seu comprovante.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
